/**
 * Phase 10 R5 — useTicketFinancialWarnings
 *
 * Composes the existing R4 ticket financial summary with the shop's R5
 * targets and low-margin threshold to produce a sorted warning array.
 *
 * Pure composition over existing hooks — no new queries.
 */
import { useMemo } from 'react';
import { useTicketFinancialSummary } from '@/hooks/labor/useTicketFinancialSummary';
import { useShopFinancialSettings } from '@/hooks/labor/useShopFinancialSettings';
import {
  computeFinancialWarnings,
  worstSeverity,
  type FinancialWarning,
  type WarningSeverity,
} from '@/lib/labor/computeFinancialWarnings';

export interface UseTicketFinancialWarningsResult {
  warnings: FinancialWarning[];
  severity: WarningSeverity | null;
  isLoading: boolean;
  enabled: boolean;
}

export function useTicketFinancialWarnings(
  ticketId: string | undefined,
): UseTicketFinancialWarningsResult {
  const { summary, isLoading: summaryLoading, enabled } =
    useTicketFinancialSummary(ticketId);
  const { settings, isLoading: settingsLoading } = useShopFinancialSettings();

  const warnings = useMemo(() => {
    if (!summary) return [];
    return computeFinancialWarnings(summary, {
      parts: settings.target_parts_gp_percent,
      labor: settings.target_labor_gp_percent,
      total: settings.target_total_gp_percent,
      lowMargin: settings.low_margin_warn_threshold,
    });
  }, [
    summary,
    settings.target_parts_gp_percent,
    settings.target_labor_gp_percent,
    settings.target_total_gp_percent,
    settings.low_margin_warn_threshold,
  ]);

  return {
    warnings,
    severity: worstSeverity(warnings),
    isLoading: summaryLoading || settingsLoading,
    enabled,
  };
}
